import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Printer, Search, Trash2, Wand2, X } from "lucide-react";
import JsBarcode from "jsbarcode";

const MAROON = "#8E2A3C";
const inp = "h-9 px-2.5 rounded-lg bg-white border border-slate-300 text-[12px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-100 focus:border-[#8E2A3C]";
const BARCODE_TYPES = [
  { id: "C128", label: "C128" }, { id: "EAN13", label: "EAN-13" }, { id: "EAN8", label: "EAN-8" },
  { id: "C39", label: "Code 39" }, { id: "UPC", label: "UPC-A" },
];
const PAPER_TYPES = [
  { id: "tj108", label: "TJ-108 (33×25mm)" }, { id: "tj109", label: "TJ-109 (50×30mm)" },
  { id: "tj110", label: "TJ-110 (40×20mm)" }, { id: "a4", label: "Kertas A4" },
];
const JSBC_FMT = { C128: "CODE128", C39: "CODE39", EAN13: "EAN13", EAN8: "EAN8", UPC: "UPC" };

const DEFAULTS = {
  bar_height: 28, bar_width: 1, barcode_type: "C128", paper_type: "tj108",
  hide_sku: true, hide_name: true, hide_price: false, sku_top: true,
  name_bottom: false, alt_name: false, use_online_price: false,
  custom_enabled: true,
  box_h: 70, box_w: 122, box_margin: 3, box_padding: 3,
  margin_top: 3, margin_bottom: 3, margin_left: 3, margin_right: 6,
  paper_w: 378, boxes_per_row: 3,
};

export default function AdminBarcodePrint() {
  const [products, setProducts] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [cfg, setCfg] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    api.get("/products", { params: { limit: 200 } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/barcode/print-settings").then((r) => setCfg({ ...DEFAULTS, ...r.data })).catch(() => {});
  }, []);

  const setC = (k, v) => setCfg((s) => ({ ...s, [k]: v }));

  const keyOf = (prod, v) => `${prod.id}::${v ? v.sku : "_"}`;
  const isAdded = (prod, v) => items.some((it) => it.key === keyOf(prod, v));

  // tambah variasi TERTENTU yang dipilih (bukan semua variasi sekaligus)
  const addVariant = (prod, v) => {
    if (isAdded(prod, v)) return toast.info("Variasi ini sudah ada di daftar");
    setItems((s) => [...s, { key: keyOf(prod, v), product: prod, variant: v, qty: 1 }]);
    setPickerOpen(false);
    setSearch("");
  };

  const setQty = (key, qty) => setItems((s) => s.map((it) => it.key === key ? { ...it, qty: Math.max(1, parseInt(qty) || 1) } : it));
  const removeItem = (key) => setItems((s) => s.filter((it) => it.key !== key));

  // generate barcode pendek (5 karakter) lalu simpan ke produk/variasi di DB
  const [shortening, setShortening] = useState("");
  const shortenBarcode = async (it) => {
    setShortening(it.key);
    try {
      const r = await api.post("/admin/barcode/shorten", { product_id: it.product.id, sku: it.variant ? it.variant.sku : null });
      const newCode = r.data.barcode;
      setItems((s) => s.map((x) => x.key !== it.key ? x : (x.variant ? { ...x, variant: { ...x.variant, barcode: newCode } } : { ...x, product: { ...x.product, barcode: newCode } })));
      setProducts((s) => s.map((p) => p.id !== it.product.id ? p : { ...p, barcode: r.data.scope === "product" ? newCode : p.barcode, variants: (p.variants || []).map((v) => v.sku === it.variant?.sku ? { ...v, barcode: newCode } : v) }));
      toast.success(`Barcode pendek ${newCode} disimpan ke ${it.variant ? "variasi" : "produk"} — konsisten dengan scanner kasir`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat barcode pendek"); }
    finally { setShortening(""); }
  };
  const isLong = (it) => (codeOf(it) || "").length > 8;

  const priceOf = (it) => {
    const base = it.variant ? (it.variant.price || it.product.price) : it.product.price;
    if (cfg.use_online_price) {
      const online = it.variant?.online_price ?? it.product.online_price;
      return online != null ? online : base;
    }
    return base;
  };
  const codeOf = (it) => (it.variant ? (it.variant.barcode || it.variant.sku) : (it.product.barcode || it.product.sku)) || "NONE";
  const nameOf = (it) => (cfg.alt_name ? (it.product.alt_name || it.product.name) : it.product.name);
  const subOf = (it) => it.variant ? `${it.variant.color || ""}${it.variant.color && it.variant.size ? " / " : ""}${it.variant.size || ""}` : "";

  const labels = useMemo(() => {
    const out = [];
    items.forEach((it) => {
      for (let k = 0; k < (parseInt(it.qty) || 1); k++) out.push({ ...it, uid: `${it.key}#${k}` });
    });
    return out;
  }, [items]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/admin/barcode/print-settings", cfg);
      toast.success("Pengaturan barcode disimpan — akan dipakai lagi saat cetak berikutnya");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan"); }
    finally { setSaving(false); }
  };

  const doPrint = () => {
    if (!labels.length) return toast.error("Tambahkan produk dulu");
    window.print();
  };

  const filtered = products.filter((p) => !search.trim() || p.name.toLowerCase().includes(search.trim().toLowerCase()));

  const boxStyle = cfg.custom_enabled
    ? {
        width: `${cfg.box_w}px`, height: `${cfg.box_h}px`,
        margin: `${cfg.box_margin}px`, padding: `${cfg.box_padding}px`,
        paddingTop: `${cfg.margin_top}px`, paddingBottom: `${cfg.margin_bottom}px`,
        paddingLeft: `${cfg.margin_left}px`, paddingRight: `${cfg.margin_right}px`,
      }
    : { width: "31mm", minHeight: "23mm", margin: "1.5mm", padding: "1.5mm" };

  const previewW = cfg.custom_enabled ? `${cfg.paper_w}px` : "100%";

  return (
    <div data-testid="barcode-print-page" className="space-y-4 pb-10 max-w-5xl">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #bcprint-area, #bcprint-area * { visibility: visible !important; }
        #bcprint-area { position: absolute; left: 0; top: 0; padding: 0 !important; margin: 0 !important; width: auto !important; }
        .bc-box { border: none !important; }
        @page { margin: 4mm; }
      }`}</style>

      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
        <h1 className="font-display text-xl font-bold text-slate-900" data-testid="bc-title">Cetak Barcode Produk</h1>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <button data-testid="bc-add-product" onClick={() => setPickerOpen(true)}
          className="h-10 px-4 rounded-lg text-white text-[12px] font-bold flex items-center gap-1.5 transition-colors hover:opacity-90" style={{ background: MAROON }}>
          <Plus className="w-4 h-4" /> Tambah Produk
        </button>
        <div>
          <div className="grid grid-cols-[1fr_120px_40px] gap-3 text-[11px] font-semibold text-slate-500 pb-2 border-b border-slate-100">
            <span>Nama</span><span className="text-center">Qty</span><span />
          </div>
          {items.length === 0 && <div className="py-8 text-center text-xs text-slate-400">Belum ada produk — klik "Tambah Produk".</div>}
          <div className="divide-y divide-slate-50">
            {items.map((it) => (
              <div key={it.key} data-testid={`bc-item-${it.key}`} className="grid grid-cols-[1fr_120px_40px] gap-3 items-center py-2.5">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">{it.product.name}</div>
                  {it.variant && <div className="text-[10px] text-slate-400">{subOf(it)} • <span className="font-mono">{codeOf(it)}</span></div>}
                  {!it.variant && <div className="text-[10px] text-slate-400 font-mono">{codeOf(it)}</div>}
                  {isLong(it) && (
                    <button data-testid={`bc-shorten-${it.key}`} onClick={() => shortenBarcode(it)} disabled={shortening === it.key}
                      className="mt-1 h-6 px-2 rounded-md bg-amber-50 text-amber-600 text-[9px] font-bold inline-flex items-center gap-1 hover:bg-amber-100 transition-colors disabled:opacity-50">
                      <Wand2 className="w-3 h-3" /> {shortening === it.key ? "Membuat..." : "Buat Barcode Pendek"}
                    </button>
                  )}
                </div>
                <input data-testid={`bc-qty-${it.key}`} type="number" min="1" max="999" value={it.qty}
                  onChange={(e) => setQty(it.key, e.target.value)} className={`${inp} text-center`} />
                <button data-testid={`bc-remove-${it.key}`} onClick={() => removeItem(it.key)} className="w-8 h-8 rounded-lg text-red-400 hover:bg-red-50 flex items-center justify-center transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <h2 className="text-[13px] font-bold text-slate-800">Atur Tampilan Barcode</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Tinggi barcode"><input data-testid="cfg-bar-height" type="number" value={cfg.bar_height} onChange={(e) => setC("bar_height", e.target.value)} className={`${inp} w-full`} /></Field>
          <Field label="Lebar barcode"><input data-testid="cfg-bar-width" type="number" step="0.1" value={cfg.bar_width} onChange={(e) => setC("bar_width", e.target.value)} className={`${inp} w-full`} /></Field>
          <Field label="Tipe barcode">
            <select data-testid="cfg-barcode-type" value={cfg.barcode_type} onChange={(e) => setC("barcode_type", e.target.value)} className={`${inp} w-full`}>
              {BARCODE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Tipe kertas">
            <select data-testid="cfg-paper-type" value={cfg.paper_type} onChange={(e) => setC("paper_type", e.target.value)} className={`${inp} w-full`}>
              {PAPER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2.5">
          <Check tid="cfg-hide-sku" label="Sembunyikan SKU produk" v={cfg.hide_sku} set={(v) => setC("hide_sku", v)} />
          <Check tid="cfg-hide-name" label="Sembunyikan nama produk" v={cfg.hide_name} set={(v) => setC("hide_name", v)} />
          <Check tid="cfg-hide-price" label="Sembunyikan harga produk" v={cfg.hide_price} set={(v) => setC("hide_price", v)} />
          <Check tid="cfg-sku-top" label="SKU di atas" v={cfg.sku_top} set={(v) => setC("sku_top", v)} />
          <Check tid="cfg-name-bottom" label="Nama produk dibawah" v={cfg.name_bottom} set={(v) => setC("name_bottom", v)} />
          <Check tid="cfg-alt-name" label="Gunakan nama alternatif" v={cfg.alt_name} set={(v) => setC("alt_name", v)} />
          <Check tid="cfg-online-price" label="Gunakan harga jual online" v={cfg.use_online_price} set={(v) => setC("use_online_price", v)} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center gap-3">
          <h2 className="text-[13px] font-bold text-slate-800">Gunakan custom barcode</h2>
          <button data-testid="cfg-custom-toggle" onClick={() => setC("custom_enabled", !cfg.custom_enabled)}
            className={`relative w-11 h-6 rounded-full transition-colors ${cfg.custom_enabled ? "bg-blue-500" : "bg-slate-300"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${cfg.custom_enabled ? "translate-x-[22px]" : "translate-x-0.5"}`} />
          </button>
          <span className="text-[11px] font-semibold text-slate-500">{cfg.custom_enabled ? "Aktif" : "Non-aktif"}</span>
        </div>
        {cfg.custom_enabled && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Tinggi box (px)"><input data-testid="cfg-box-h" type="number" value={cfg.box_h} onChange={(e) => setC("box_h", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Lebar box (px)"><input data-testid="cfg-box-w" type="number" value={cfg.box_w} onChange={(e) => setC("box_w", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Margin box (px)"><input data-testid="cfg-box-margin" type="number" value={cfg.box_margin} onChange={(e) => setC("box_margin", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Padding box (px)"><input data-testid="cfg-box-padding" type="number" value={cfg.box_padding} onChange={(e) => setC("box_padding", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Margin Atas"><input data-testid="cfg-margin-top" type="number" value={cfg.margin_top} onChange={(e) => setC("margin_top", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Margin Bawah"><input data-testid="cfg-margin-bottom" type="number" value={cfg.margin_bottom} onChange={(e) => setC("margin_bottom", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Margin Kiri"><input data-testid="cfg-margin-left" type="number" value={cfg.margin_left} onChange={(e) => setC("margin_left", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Margin Kanan"><input data-testid="cfg-margin-right" type="number" value={cfg.margin_right} onChange={(e) => setC("margin_right", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Lebar Kertas (px)"><input data-testid="cfg-paper-w" type="number" value={cfg.paper_w} onChange={(e) => setC("paper_w", e.target.value)} className={`${inp} w-full`} /></Field>
            <Field label="Jumlah box tiap baris"><input data-testid="cfg-boxes-per-row" type="number" min="1" max="20" value={cfg.boxes_per_row} onChange={(e) => setC("boxes_per_row", e.target.value)} className={`${inp} w-full`} /></Field>
          </div>
        )}
        <p className="text-[10px] text-slate-400">Pengaturan anda akan disimpan secara otomatis untuk digunakan kembali pada saat anda akan melakukan cetak barcode lagi.</p>
        <button data-testid="cfg-save" onClick={saveSettings} disabled={saving}
          className="h-9 px-4 rounded-lg text-white text-[12px] font-bold transition-colors hover:opacity-90 disabled:opacity-50" style={{ background: MAROON }}>
          {saving ? "Menyimpan..." : "Simpan"}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h2 className="text-[13px] font-bold text-slate-800">Print Preview</h2>
        <p className="text-[10px] text-slate-400">Untuk hasil cetak yang sesuai, anda harus menyesuaikan pengaturan lainnya pada pengaturan printer di browser anda.</p>
        <div className="overflow-x-auto">
          <div id="bcprint-area" data-testid="bc-preview" ref={previewRef} style={{ width: previewW }}
            className="flex flex-wrap content-start min-h-[90px]">
            {labels.map((l) => (
              <LabelBox key={l.uid} item={l} cfg={cfg} style={boxStyle} nameOf={nameOf} subOf={subOf} codeOf={codeOf} priceOf={priceOf} />
            ))}
            {labels.length === 0 && <div className="w-full py-10 text-center text-xs text-slate-400">Tambahkan produk untuk melihat pratinjau label.</div>}
          </div>
        </div>
        <button data-testid="bc-print-btn" onClick={doPrint}
          className="h-10 px-5 rounded-lg text-white text-[12px] font-bold flex items-center gap-1.5 transition-colors hover:opacity-90" style={{ background: MAROON }}>
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPickerOpen(false)}>
          <div data-testid="bc-picker-modal" className="bg-white rounded-2xl p-5 w-full max-w-lg space-y-3 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-sm text-slate-900">Tambah Produk</h3>
              <button data-testid="bc-picker-close" onClick={() => setPickerOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="bc-picker-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari produk..." className={`${inp} w-full pl-8 h-10`} autoFocus />
            </div>
            <div className="overflow-y-auto divide-y divide-slate-50 -mx-1 px-1">
              {filtered.map((p) => {
                const vs = (p.variants || []).length ? p.variants : [null];
                return (
                  <div key={p.id} className="py-2.5">
                    <div className="flex items-center gap-3 px-2">
                      <span className="w-9 h-9 rounded-lg bg-rose-100 text-[#8E2A3C] flex items-center justify-center text-[10px] font-extrabold shrink-0 overflow-hidden">
                        {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : p.name.slice(0, 2).toUpperCase()}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[12px] font-semibold text-slate-800 truncate">{p.name}</span>
                        <span className="block text-[10px] text-slate-400">{formatIDR(p.price)}</span>
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2 pl-2">
                      {vs.map((v) => {
                        const added = isAdded(p, v);
                        const lbl = v ? `${v.color || ""}${v.color && v.size ? " / " : ""}${v.size || ""}` : "Tanpa variasi";
                        return (
                          <button key={v ? v.sku : "_"} data-testid={`bc-pick-${p.id}-${v ? v.sku : "base"}`} onClick={() => !added && addVariant(p, v)} disabled={added}
                            className={`h-8 px-3 rounded-lg text-[11px] font-semibold border transition-colors ${added ? "bg-emerald-50 text-emerald-600 border-emerald-200 cursor-default" : "border-slate-200 text-slate-600 hover:border-[#8E2A3C] hover:text-[#8E2A3C]"}`}>
                            {added ? "✓ " : "+ "}{lbl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {!filtered.length && <div className="py-8 text-center text-xs text-slate-400">Produk tidak ditemukan.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Check({ tid, label, v, set }) {
  return (
    <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
      <input data-testid={tid} type="checkbox" checked={v} onChange={(e) => set(e.target.checked)} className="w-3.5 h-3.5 rounded border-slate-300 accent-[#8E2A3C]" />
      {label}
    </label>
  );
}

function LabelBox({ item, cfg, style, nameOf, subOf, codeOf, priceOf }) {
  const showSku = !cfg.hide_sku;
  const showName = !cfg.hide_name;
  const showPrice = !cfg.hide_price;
  const code = codeOf(item);
  const svgRef = useRef(null);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    try {
      el.innerHTML = "";
      JsBarcode(el, String(code), {
        format: JSBC_FMT[cfg.barcode_type] || "CODE128",
        displayValue: false,
        height: Math.max(12, parseInt(cfg.bar_height) || 28),
        width: Math.max(0.5, parseFloat(cfg.bar_width) || 1),
        margin: 0,
        background: "#ffffff",
      });
    } catch { /* kode tidak cocok dengan tipe barcode */ }
  }, [code, cfg.barcode_type, cfg.bar_height, cfg.bar_width]);
  return (
    <div data-testid={`bc-label-${item.uid}`} className="bc-box bg-white flex flex-col items-center justify-center text-center overflow-hidden shrink-0"
      style={style}>
      {cfg.sku_top && showSku && <div className="text-[7px] font-mono text-slate-600 leading-tight">{code}</div>}
      {showName && !cfg.name_bottom && <div className="text-[7px] font-semibold text-slate-800 leading-tight line-clamp-1 w-full">{nameOf(item)}</div>}
      {item.variant && showName && <div className="text-[6px] text-slate-500 leading-tight">{subOf(item)}</div>}
      <svg ref={svgRef} className="max-w-full" style={{ height: `${Math.max(14, (parseInt(cfg.bar_height) || 28) * 0.9)}px` }} />
      {!cfg.sku_top && showSku && <div className="text-[7px] font-mono text-slate-800 leading-tight">{code}</div>}
      {showName && cfg.name_bottom && <div className="text-[7px] font-semibold text-slate-800 leading-tight line-clamp-1 w-full">{nameOf(item)}</div>}
      {showPrice && <div className="text-[7px] font-bold text-slate-900 leading-tight">{formatIDR(priceOf(item))}</div>}
    </div>
  );
}
